// PURE logic for the showcase lifecycle surface. No React, no FireCMS imports — so "what exactly does
// this write, and what exactly does it refuse?" is answerable without reading any UI code. Same
// separation the onboarding surface keeps between `*.utils.ts` and `*.write.ts`.
//
// ⚠️ EVERY CHECK HERE IS ADVISORY, AND THE COPY MUST NEVER IMPLY OTHERWISE. The backend re-reads the
// showcase from Apple, re-assesses whether it is live, and re-applies the extension bound against
// Apple's OWN reported original start date before it calls anything. These functions exist to give the
// answer BEFORE the write rather than after, and to make the confirmation text specific. Where this
// file and the backend disagree, the backend is right.

import {
  EnvScopedShowcaseState,
  OnboardingShowcaseRequestEntity,
} from "./apple_delegated_onboarding.types";
import {
  LIFECYCLE_CMS_OWNED_FIELDS,
  LifecycleSubmitStateEnum,
  ShowcaseLifecycleActionEnum,
  ShowcaseLifecycleRequestEntity,
} from "./apple_showcase_lifecycle.types";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Apple's extension window in days, mirroring the backend's `SHOWCASE_EXTENSION_WINDOW_DAYS`.
 *
 * ⚠️ THIS IS *NOT* THE 30-DAY CREATE CAP, EVEN THOUGH BOTH ARE 30. The create cap limits how long a
 * NEW showcase may run. This is an ANCHOR: when the original flight window was under 30 days, a new
 * end date must be no more than 30 days from the showcase's ORIGINAL start date. Two different rules
 * with two different sources that happen to share a number — see the backend constant's docblock.
 */
export const SHOWCASE_EXTENSION_WINDOW_DAYS = 30;

/** UTC midnight of a date-only or date-time ISO string. `undefined` for anything unparseable. */
export const parseIsoDate = (value?: string | null): Date | undefined => {
  if (typeof value !== "string" || value.trim().length === 0) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const midnight = new Date(parsed);
  midnight.setUTCHours(0, 0, 0, 0);
  return midnight;
};

/** `YYYY-MM-DD`, which is how every date in this surface's copy is written. */
export const asUtcDay = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * The last day a showcase is actually visible.
 *
 * ⚠️ `endDate` IS EXCLUSIVE — the showcase is NOT visible on it. Every confirmation in this surface
 * names this day rather than the raw end date, because "runs until Oct 6" and "last visible Oct 5"
 * are the difference between an operator understanding what they are cancelling and not.
 */
export const lastVisibleDay = (endExclusive: Date): string =>
  asUtcDay(new Date(endExclusive.getTime() - MS_PER_DAY));

/**
 * The latest end date an extension may ask for: 30 days from the ORIGINAL start date.
 *
 * ⚠️ NOT 30 DAYS FROM TODAY, AND NOT 30 DAYS FROM THE CURRENT END DATE. A 7-day showcase starting
 * Mar 1 can be extended to at most Mar 31 however late the request is made and however many times it
 * has already been extended.
 */
export const maxExtendedEndDate = (originalStart: Date): Date =>
  new Date(originalStart.getTime() + SHOWCASE_EXTENSION_WINDOW_DAYS * MS_PER_DAY);

/**
 * The env-scoped Apple result for a showcase request, or `undefined` when there is none.
 *
 * ⚠️ `env` MAY BE `null`, and a `null` environment must yield NO result rather than a guessed one.
 * `resolveDocumentEnvironment` returns `null` when no step has reported an Apple observation yet, and
 * defaulting to (say) DQE there would show a DQE showcase id on a production record — then offer a
 * delete button next to it.
 */
export const showcaseAppleResult = (
  step: Partial<OnboardingShowcaseRequestEntity> | null | undefined,
  env: string | null | undefined,
): EnvScopedShowcaseState | undefined =>
  env
    ? (step?.apple as Record<string, EnvScopedShowcaseState> | null | undefined)?.[env]
    : undefined;

/**
 * Everything a confirmation dialog needs to name the showcase it is about to act on. `showcaseId`
 * being `undefined` is the "there is nothing to act on" case, and it is the FIRST thing the gate
 * checks.
 */
export interface ShowcaseSubject {
  showcaseId?: string;
  creativeId?: string;
  locationId?: string;
  brandId?: string;
  /** The window WE submitted. Apple's own record is authoritative; the backend reads it. */
  startDate?: string;
  endDate?: string;
}

export const describeSubject = (
  step: Partial<OnboardingShowcaseRequestEntity> | null | undefined,
  env: string | null | undefined,
): ShowcaseSubject => {
  const apple = showcaseAppleResult(step, env);
  return {
    showcaseId: apple?.showcaseId ?? undefined,
    creativeId: apple?.creativeId ?? undefined,
    locationId: step?.input?.locationId,
    brandId: step?.input?.brandId,
    startDate: step?.input?.startDate,
    endDate: step?.input?.endDate,
  };
};

/**
 * A human sentence naming the showcase and its dates, for the confirmation copy.
 *
 * ⚠️ IT ALWAYS SAYS WHICH DAY IS THE LAST VISIBLE ONE, and it says so from the exclusive end date, so
 * an operator who has been thinking of the end date inclusively notices the off-by-one HERE rather
 * than after a real card went dark a day early.
 */
export const describeFlightWindow = (subject: ShowcaseSubject): string => {
  const start = parseIsoDate(subject.startDate);
  const end = parseIsoDate(subject.endDate);
  if (!start || !end) {
    return "Its flight window is not recorded here — check it in Apple Business Connect before you continue.";
  }
  return `It runs from ${asUtcDay(start)}, and its last visible day is ${lastVisibleDay(end)} (the end date ${asUtcDay(end)} is EXCLUSIVE).`;
};

export interface LifecycleGate {
  allowed: boolean;
  /** Present when refused. Says what is missing and what to do instead. */
  reason?: string;
  /** Non-blocking warnings the dialog shows before the confirm button. */
  cautions: string[];
}

/**
 * Should this action even be offered? Advisory, in the CMS, before the write.
 *
 * ⚠️ IT DELIBERATELY DOES **NOT** TRY TO DECIDE WHETHER THE SHOWCASE IS LIVE. That decision needs
 * Apple's CURRENT state and CURRENT window, and this console has neither — only what a poller last
 * recorded, which can be permanently stale (the showcase poller's 24-hour lookback against Apple's
 * 7-day feedback window loses events outright). Guessing liveness here would produce a confident
 * "you can delete this" that the backend then refuses, which is worse than not guessing: it trains
 * operators to expect the gate to be wrong. So the CMS gates on what it can KNOW — is there a
 * showcase id, is an extension date present and within the bound — and states plainly that the live
 * check happens server-side.
 */
export const evaluateLifecycleGate = (params: {
  action: ShowcaseLifecycleActionEnum;
  subject: ShowcaseSubject;
  /** EXTEND only. */
  requestedEndDate?: string;
}): LifecycleGate => {
  const { action, subject, requestedEndDate } = params;
  const cautions: string[] = [];

  if (!subject.showcaseId) {
    return {
      allowed: false,
      cautions,
      reason:
        "This request has no Apple showcase id recorded for the current environment, so there is " +
        "nothing at Apple to act on. If the showcase was created, wait for the result to appear on " +
        "this record; if it was never created, there is nothing to remove.",
    };
  }

  if (action !== ShowcaseLifecycleActionEnum.EXTEND) {
    cautions.push(
      "Whether Apple permits this depends on the showcase being live right now. The backend reads " +
        "the showcase from Apple and checks that before it sends anything — if it refuses, nothing " +
        "will have been changed.",
    );
    return { allowed: true, cautions };
  }

  const newEnd = parseIsoDate(requestedEndDate);
  if (!newEnd) {
    return {
      allowed: false,
      cautions,
      reason: "Enter the new end date as a UTC-midnight ISO date, e.g. 2026-03-31T00:00:00Z.",
    };
  }

  const currentEnd = parseIsoDate(subject.endDate);
  if (currentEnd && newEnd.getTime() <= currentEnd.getTime()) {
    return {
      allowed: false,
      cautions,
      reason:
        `An extension has to move the end date LATER. This showcase already ends ${asUtcDay(currentEnd)} ` +
        `(exclusive — last visible day ${lastVisibleDay(currentEnd)}).`,
    };
  }

  const originalStart = parseIsoDate(subject.startDate);
  if (!originalStart) {
    cautions.push(
      "This record has no start date, so the extension limit could not be checked here. The backend " +
        "checks it against the start date Apple itself reports.",
    );
    return { allowed: true, cautions };
  }

  const maxEnd = maxExtendedEndDate(originalStart);
  if (newEnd.getTime() > maxEnd.getTime()) {
    return {
      allowed: false,
      cautions,
      reason:
        `Apple measures an extension from the showcase's ORIGINAL start date (${asUtcDay(originalStart)}) — ` +
        `not from today, and not from its current end date. The latest end date this showcase can ` +
        `have is ${asUtcDay(maxEnd)}, so its last visible day can be at most ${lastVisibleDay(maxEnd)}. ` +
        `To run longer, let this showcase end and create a new one.`,
    };
  }

  cautions.push(
    `Apple only permits extending a LIVE showcase. The backend checks that, and re-checks this ${SHOWCASE_EXTENSION_WINDOW_DAYS}-day ` +
      `limit against the start date Apple itself reports — which is the authority if it differs from ` +
      `the ${asUtcDay(originalStart)} recorded here.`,
  );
  cautions.push(
    "It also checks that the longer window does not overlap another showcase scheduled on this " +
      "location, because Apple shows one showcase per location at a time.",
  );
  return { allowed: true, cautions };
};

/**
 * The exact document this console writes to enqueue one lifecycle action.
 *
 * ⚠️ IT SNAPSHOTS WHAT THE OPERATOR WAS LOOKING AT (`confirmed*`). For an action with no undo, the
 * question "did we act on the right showcase?" has to be answerable later, and the ids the operator
 * saw are the only evidence of that. The backend re-resolves everything and must never trust them.
 *
 * ⚠️ IT WRITES `submitState: QUEUED` AND NOTHING ELSE OF THE BACKEND'S. No result field, no
 * `contractState` — those are the backend's, and the whole point of the ownership split is that a
 * stale form value can never overwrite one.
 */
export const buildLifecycleRequestValues = (params: {
  action: ShowcaseLifecycleActionEnum;
  subject: ShowcaseSubject;
  requestedEndDate?: string;
  deleteCreative?: boolean;
  requestedBy?: string;
  now: Date;
}): ShowcaseLifecycleRequestEntity => {
  const { action, subject, now } = params;
  return {
    action,
    requestedEndDate:
      action === ShowcaseLifecycleActionEnum.EXTEND ? (params.requestedEndDate ?? null) : null,
    deleteCreative:
      action === ShowcaseLifecycleActionEnum.DELETE ? (params.deleteCreative ?? false) : null,
    confirmedShowcaseId: subject.showcaseId ?? null,
    confirmedLocationId: subject.locationId ?? null,
    confirmedBrandId: subject.brandId ?? null,
    confirmedStartDate: subject.startDate ?? null,
    confirmedEndDate: subject.endDate ?? null,
    submitState: LifecycleSubmitStateEnum.QUEUED,
    requestedAt: now,
    requestedBy: params.requestedBy ?? null,
    writeError: null,
  };
};

/** `writeError` for an enqueue that could not even be written. Mirrors the onboarding surface. */
/**
 * The record left behind when the enqueue write itself fails.
 *
 * ⚠️ IT CARRIES THE ATTEMPTED VALUES, AND IT USED TO CARRY ONLY `writeError`. A lifecycle history is
 * the audit trail for actions with no undo, so an entry saying only "this failed" — without which
 * action, against which showcase, by whom — is a row nobody can act on, and it rendered as an almost
 * empty line. The values are the ones actually attempted rather than re-derived, so the record cannot
 * drift from what was tried.
 *
 * ⚠️ `submitState` IS FORCED TO `null`, AND THAT IS LOAD-BEARING. The backend drain claims work with
 * `submitState == 'QUEUED'`; spreading the attempted values would otherwise carry `QUEUED` in and turn
 * a FAILED write into a claimable request — the backend acting on something the console never
 * successfully queued. `null` is written affirmatively rather than the field being omitted, so the
 * intent is visible in the document instead of inferred from an absence.
 */
export const buildLifecycleWriteErrorValues = (
  attempted: ShowcaseLifecycleRequestEntity,
  message: string,
): ShowcaseLifecycleRequestEntity => ({
  ...attempted,
  submitState: null,
  writeError: message,
});

/**
 * Drop every field this console does not own, on save.
 *
 * The read-only properties stop a human EDITING a backend-owned field; this stops a SAVE echoing the
 * values the form loaded back over a fresher backend write. Both halves are needed — the identical
 * argument `stripFieldsTheFormMayNotWrite` makes in the onboarding surface.
 */
export const stripLifecycleFieldsTheFormMayNotWrite = (
  values: Partial<ShowcaseLifecycleRequestEntity>,
): Partial<ShowcaseLifecycleRequestEntity> => {
  // The ONE list, from the types module. A second copy here is exactly how an allow-list and the
  // fields it is supposed to describe drift apart.
  const allowed = new Set<string>(LIFECYCLE_CMS_OWNED_FIELDS);
  return Object.fromEntries(Object.entries(values).filter(([key]) => allowed.has(key)));
};
